// ui
import {
  Button,
  Typography,
  Modal,
  Box,
  Alert,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

import { useWindowSize } from "../../lib/useWindowSize";

export type CancellSmsItem = {
  id: number;
  cancell_sms_name: string;
  cancell_sms_type: number;
  cancell_sms_sender: string;
  cancell_sms_body: string;
  cancell_sms_title: string;
  source: "product" | "outside_entry_form";
};

interface DataCancelModalProps {
  onModal: boolean;
  swCloseModal: () => void;
  cancelDataInf: {
    status: null | "loading" | "open" | "success" | "failed";
    key: { [key: string]: string };
    message: string;
    cancellAction: string;
    cancellSmsList: CancellSmsItem[];
    selectedCancellSms: { id: number; source: "product" | "outside_entry_form" } | null;
    destinationPhoneNumber: string;
    destinationMailAddress: string;
  };
  setCancelDataInf: React.Dispatch<
    React.SetStateAction<{
      status: null | "loading" | "open" | "success" | "failed";
      key: { [key: string]: string };
      message: string;
      cancellAction: string;
      cancellSmsList: CancellSmsItem[];
      selectedCancellSms: { id: number; source: "product" | "outside_entry_form" } | null;
      destinationPhoneNumber: string;
      destinationMailAddress: string;
    }>
  >;
  cancelData: () => void;
}

const SEND_NONE_VALUE = "none";
const VALUE_SEP = "\u0001";

export function DataCancelModal(props: DataCancelModalProps) {
  const { onModal, swCloseModal, cancelDataInf, setCancelDataInf, cancelData } =
    props;

  const { windowWidth } = useWindowSize();

  const style = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: windowWidth > 568 ? "500px" : "calc(100% - 82px)",
    maxHeight: "70vh",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
  };

  const hasSmsList = cancelDataInf.cancellSmsList.length > 0;
  const selectedValue =
    cancelDataInf.selectedCancellSms === null
      ? SEND_NONE_VALUE
      : `${cancelDataInf.selectedCancellSms.id}${VALUE_SEP}${cancelDataInf.selectedCancellSms.source}`;
  const selectedItem = cancelDataInf.cancellSmsList.find(
    (m) =>
      cancelDataInf.selectedCancellSms &&
      m.id === cancelDataInf.selectedCancellSms.id &&
      m.source === cancelDataInf.selectedCancellSms.source
  );

  return (
    <Modal
      open={onModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} data-testid="data-cancel-modal">
        <Box
          style={{
            fontSize: "18px",
            color: "rgb(50 50 50)",
          }}
          key="cancelTitleBox"
          className="shippori-antique-regular"
        >
          解約
        </Box>
        <Typography id="modal-modal-description" sx={{ mt: 2, mb: 5 }}>
          {cancelDataInf.status === "open" ? (
            <>
              <Alert severity="error">
                解約処理後、取り消すことはできません。
                {hasSmsList && (
                  <>
                    <br />
                    解約時にメールまたはショートメッセージを送信する場合は下記から選択してください。
                    <br />
                    「送信しない」を選択した場合は解約のみ行い、送信は行いません。
                  </>
                )}
              </Alert>
              {hasSmsList && (
                <>
                  <Box className="serch-label" sx={{ marginTop: "10px" }}>
                    送信内容
                  </Box>
                  <FormControl
                    fullWidth
                    sx={{
                      marginTop: "10px",
                      marginBottom: "20px",
                      marginLeft: "5px",
                      width: "calc(100% - 10px)",
                    }}
                  >
                    <Select
                      data-testid="data-cancel-select"
                      labelId="cancell-sms-select-label"
                      id="cancell-sms-select"
                      value={selectedValue}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        if (v === SEND_NONE_VALUE) {
                          setCancelDataInf((prev) => ({
                            ...prev,
                            selectedCancellSms: null,
                          }));
                        } else {
                          const [idStr, source] = v.split(VALUE_SEP);
                          const id = parseInt(idStr, 10);
                          if (!isNaN(id) && (source === "product" || source === "outside_entry_form")) {
                            setCancelDataInf((prev) => ({
                              ...prev,
                              selectedCancellSms: { id, source },
                            }));
                          }
                        }
                      }}
                      sx={{
                        height: "30px",
                        textAlign: "end",
                        fontSize: "14px",
                      }}
                    >
                      <MenuItem
                        value={SEND_NONE_VALUE}
                        sx={{ fontSize: "14px", height: "24px", minHeight: "auto" }}
                      >
                        送信しない
                      </MenuItem>
                      {cancelDataInf.cancellSmsList.map((message, idx) => (
                        <MenuItem
                          key={`${message.id}-${message.source}-${idx}`}
                          value={`${message.id}${VALUE_SEP}${message.source}`}
                          sx={{ fontSize: "14px", height: "24px", minHeight: "auto" }}
                        >
                          {message.cancell_sms_name}
                          {message.cancell_sms_type === 0
                            ? "（ショートメッセージ）"
                            : "（メール）"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedItem && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                      {selectedItem.cancell_sms_type === 0 ? (
                        <>
                          <Box className="serch-label">送信元ID</Box>
                          <TextField
                            disabled
                            size="small"
                            value={selectedItem.cancell_sms_sender}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                          <Box className="serch-label">送信先電話番号</Box>
                          <TextField
                            disabled
                            size="small"
                            value={cancelDataInf.destinationPhoneNumber}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                          <Box className="serch-label">本文サンプル</Box>
                          <TextField
                            disabled
                            size="small"
                            value={selectedItem.cancell_sms_body}
                            multiline
                            rows={4}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                        </>
                      ) : (
                        <>
                          <Box className="serch-label">送信元メールアドレス</Box>
                          <TextField
                            disabled
                            size="small"
                            value={selectedItem.cancell_sms_sender}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                          <Box className="serch-label">送信先メールアドレス</Box>
                          <TextField
                            disabled
                            size="small"
                            value={cancelDataInf.destinationMailAddress}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                          <Box className="serch-label">タイトル</Box>
                          <TextField
                            disabled
                            size="small"
                            value={selectedItem.cancell_sms_title}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                          <Box className="serch-label">本文</Box>
                          <TextField
                            disabled
                            size="small"
                            value={selectedItem.cancell_sms_body}
                            multiline
                            rows={4}
                            sx={{ width: "calc(100% - 10px)", margin: "4px 5px" }}
                          />
                        </>
                      )}
                    </Box>
                  )}
                </>
              )}
              <Box style={{ display: windowWidth > 385 ? "flex" : undefined }}>
                <Box
                  style={{
                    marginTop: "10px",
                    height: "40px",
                    width:
                      windowWidth > 385
                        ? "calc(50% - 5px)"
                        : "calc(100% - 10px)",
                    marginLeft: windowWidth > 385 ? undefined : "5px",
                  }}
                >
                  <Button
                    data-testid="data-cancel-execute-button"
                    style={{
                      height: "40px",
                      width: "100%",
                      textTransform: "none",
                    }}
                    variant="contained"
                    onClick={cancelData}
                  >
                    <Box className="m-plus-rounded-1c-regular">解約</Box>
                    <CancelIcon
                      sx={{ marginLeft: "6px", color: "white" }}
                      fontSize="medium"
                    />
                  </Button>
                </Box>
                <Box
                  style={{
                    marginTop: "10px",
                    height: "40px",
                    width:
                      windowWidth > 385
                        ? "calc(50% - 5px)"
                        : "calc(100% - 10px)",
                    marginLeft: windowWidth > 385 ? "10px" : "5px",
                  }}
                >
                  <Button
                    data-testid="data-cancel-close-button"
                    variant="contained"
                    style={{
                      height: "40px",
                      width: "100%",
                      textTransform: "none",
                    }}
                    onClick={swCloseModal}
                  >
                    <Box className="m-plus-rounded-1c-regular">閉じる</Box>
                  </Button>
                </Box>
              </Box>
            </>
          ) : cancelDataInf.status === "success" ? (
            <>
              <Box className="shippori-antique-regular">
                解約に成功しました。
              </Box>
              <Box>
                <Button
                  data-testid="data-cancel-success-close-button"
                  style={{
                    marginTop: "30px",
                    marginLeft: windowWidth > 385 ? "15%" : "5px",
                    marginBottom: "-25px",
                    height: "40px",
                    width: windowWidth > 385 ? "70%" : "calc(100% - 10px)",
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={swCloseModal}
                >
                  <Box className="m-plus-rounded-1c-regular">閉じる</Box>
                </Button>
              </Box>
            </>
          ) : cancelDataInf.status === "failed" ? (
            <>
              <Box className="shippori-antique-regular">
                {cancelDataInf.message}
              </Box>
              <Box>
                <Button
                  data-testid="data-cancel-retry-button"
                  style={{
                    marginTop: "30px",
                    marginLeft: windowWidth > 385 ? "15%" : "5px",
                    marginBottom: "-25px",
                    height: "40px",
                    width: windowWidth > 385 ? "70%" : "calc(100% - 10px)",
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={() =>
                    setCancelDataInf((prev) => ({
                      ...prev,
                      status: "open",
                      message: "",
                    }))
                  }
                >
                  <Box className="m-plus-rounded-1c-regular">戻る</Box>
                </Button>
              </Box>
            </>
          ) : null}
        </Typography>
      </Box>
    </Modal>
  );
}
